/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2020
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import JSONObjectFactory from './JSONObjectFactory';
import Diagnostic from './Diagnostic';
import ValidationMessage from './ValidationMessage';
import MoserObject from './MoserObject';
import BaseQueryItemType from './BaseQueryItemType';
import ObjectType from './ObjectType';
import DrillGroup from './DrillGroup';
import Relationship from './Relationship';
import MoserObjectUtils from './MoserObjectUtils';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/common/generated/metadata/JSONObjectFactory (runtime) // new
//@import com/ibm/bi/platform/moser/common/validation/Diagnostic (runtime) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/ValidationMessage (runtime) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/MoserObject (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/BaseQueryItemType (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/ObjectType (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/DrillGroup (static) // new
//@import com/ibm/bi/platform/moser/common/generated/metadata/Relationship (static) // new
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (static) // getQueryItems

/**
 * A utility class to help process module validation results
 */
export default class ValidationUtils extends BaseMoserObject{

	static OK = 0;

	static INFO = 1;

	static WARNING = 2;

	static ERROR = 4;

	static RELATIONSHIP_LOOKUP = "/relationship[";

	static DRILLGROUP_LOOKUP = "/drillGroup[";



	/**
	 * Perform process validation results based on a given module
	 * @param {Object} json   {@link JSONObject}  Moser feedback results.
	 * @param {Module} module   {@link Module}  the data module to be validated on
	 * @param {ObjectFactory} objectFactory   {@link ObjectFactory}  an object factory created by the client so that notifications can be fired if the client chooses to add listeners onto it.
	 * @return {Array}  a <code>Diagnostic</code> object contains the validation results
	 */
	static validateModule(json, objectFactory, module) {
		if (!json || !objectFactory || !module) {
			return [];
		}
		module.removeDiagnostics();
		var diagnostics = [];
		var factory = new JSONObjectFactory(objectFactory);
		var feedbackValue = json["feedback"];
		var feedback;
		if (!feedbackValue) {
			feedback = factory.createFeedback(json);
		} else {
			feedback = factory.createFeedback(feedbackValue);
		}
		var data = feedback.getData();
		var vMsgs;
		if (data) {
			vMsgs = data.getValidationMessage();
			for (var __i_enFor0 = 0, __exp_enFor0 = vMsgs, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var msg = __exp_enFor0[__i_enFor0];
				var diagnostic = ValidationUtils._resolveByPath(ValidationUtils._convertLocationToList(msg.getLocation()), module, msg, module.getLabel());
				if (diagnostic) {
					diagnostics.push(diagnostic);
				}
				ValidationUtils._resolveAndTagRelationship(module, msg);
				ValidationUtils._resolveAndTagDrillGroup(module, msg);
			}
		}
		return diagnostics;
	}

	/**
	 * Validate the expression editor content
	 * @param {Object} json  the json result from endpoint /feedbackCalculation
	 * @return {Array}  A Diagnostic list.
	 */
	static validateExpression(json) {
		var diagnosticList = [];
		if (!json || json["messages"] == null) {
			return diagnosticList;
		}
		var messages = json["messages"];
		if (messages) {
			for (var __i_enFor0 = 0, __exp_enFor0 = messages, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var message = __exp_enFor0[__i_enFor0];
				var diagnostic = new Diagnostic();
				var msg = new ValidationMessage();
				var child = message;
				if (child) {
					msg.setSeverity(child["severity"]);
					msg.setMsg(child["message"]);
				}
				diagnostic.setValidationMessage(msg);
				diagnosticList.push(diagnostic);
			}
		}
		return diagnosticList;
	}

	static _convertLocationToList(location) {
		if (location == null || location.length === 0) {
			return [];
		}
		var start = location.indexOf("/") + 1;
		var segments = location.substring(start, location.length);
		var parts = segments.split(new RegExp("/"));
		var list = [];
		for (var __i_enFor0 = 0, __len_enFor0 = parts.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var part = parts[__i_enFor0];
			if (part.length > 0) {
				list.push(part);
			}
		}
		return list;
	}

	static _resolveByPath(path, module, message, label) {
		if (path.length==0 || !module || path[0].length == 0) {
			return null;
		}
		var node = path[0];
		var featureName = node;
		var arrayIdx = 0;
		var idx = node.indexOf("[");
		if (idx != -1) {
			featureName = node.substring(0, idx);
			var element = node.substring(idx + 1, node.length - 1);
			arrayIdx = parseInt(element) - 1;
		}
		var featureId = module.getFeatureID(featureName);
		var diagnostic;
		if (featureId > 0) {
			var moduleProperty = module.get(featureId);
			var modulePropElement;
			if ((typeof moduleProperty === "array" || moduleProperty instanceof Array)) {
				modulePropElement = (moduleProperty)[arrayIdx];
			} else {
				modulePropElement = moduleProperty;
			}
			if (modulePropElement instanceof MoserObject) {
				path.splice(0, 1);
				var moserObj = modulePropElement;
				var labelObjType = ValidationUtils._checkLabel(moserObj);
				var newLabel = label;
				if (labelObjType != null) {
					newLabel = labelObjType;
				}
				if (!(path.length==0) && path[0].length != 0) {
					diagnostic = ValidationUtils._resolveByPath(path, moserObj, message, newLabel);
				} else {
					diagnostic = ValidationUtils._setDiagnoticProperties(moserObj, newLabel, message, featureId);
					moserObj.addDiagnostic(diagnostic);
				}
			} else {
				diagnostic = ValidationUtils._setDiagnoticProperties(module, label, message, featureId);
			}
			module.addDiagnostic(diagnostic);
		} else if ("sortItem" == featureName && module instanceof BaseQueryItemType) {
			var qi = module;
			diagnostic = ValidationUtils._setDiagnoticProperties(qi, label, message, 249);
			qi.addDiagnostic(diagnostic);
			var facetDef = qi.getFacetDefinition();
			if (facetDef) {
				diagnostic = ValidationUtils._setDiagnoticProperties(facetDef, label, message, 18);
				facetDef.addDiagnostic(diagnostic);
				var sortList = facetDef.getSortList();
				if (sortList) {
					diagnostic = ValidationUtils._setDiagnoticProperties(sortList, label, message, 183);
					sortList.addDiagnostic(diagnostic);
					var sortItemList = sortList.basicGetSortItem();
					if (sortItemList && !(sortItemList.length==0)) {
						var sorItem = sortItemList[0];
						diagnostic = ValidationUtils._setDiagnoticProperties(sorItem, label, message, 36);
						sorItem.addDiagnostic(diagnostic);
					}
				}
			}
		} else {
			diagnostic = ValidationUtils._setDiagnoticProperties(module, label, message, featureId);
			module.addDiagnostic(diagnostic);
		}
		return diagnostic;
	}

	static _setDiagnoticProperties(object, label, message, featureId) {
		var diagnostic = new Diagnostic();
		diagnostic.setProperties(object, label, message, featureId);
		return diagnostic;
	}

	static _checkLabel(obj) {
		var label;
		if (obj instanceof ObjectType) {
			var labelObjType = (obj).getLabel();
			if (labelObjType != null) {
				label = labelObjType;
			}
		}
		return label;
	}

	static _getObjectByLocationPath(module, path) {
		var featureName = path;
		var arrayIdx = 0;
		var idx = path.indexOf("[");
		if (idx != -1) {
			featureName = path.substring(0, idx);
			var element = path.substring(idx + 1, path.length - 1);
			arrayIdx = parseInt(element) - 1;
		}
		var featureId = module.getFeatureID(featureName);
		var moduleProperty = module.get(featureId);
		var modulePropElement;
		if ((typeof moduleProperty === "array" || moduleProperty instanceof Array)) {
			modulePropElement = (moduleProperty)[arrayIdx];
		} else {
			modulePropElement = moduleProperty;
		}
		return modulePropElement;
	}

	static _resolveAndTagDrillGroup(module, message) {
		var location = message.getLocation();
		if (location == null || location.indexOf("/drillGroup[") == -1) {
			return;
		}
		var locationList = ValidationUtils._convertLocationToList(location);
		if (locationList.length <= 1) {
			return;
		}
		var diagnostic;
		var drillGroup = ValidationUtils._getObjectByLocationPath(module, locationList[0]);
		if (drillGroup instanceof MoserObject) {
			var segment = ValidationUtils._getObjectByLocationPath(drillGroup, locationList[1]);
			if (segment instanceof DrillGroup.Segment) {
				var seg = segment;
				var identifier = seg.getRef();
				var index = identifier.indexOf(".");
				var qsId = identifier.substring(0, index);
				var qiId = identifier.substring(index + 1, identifier.length - 1);
				var querySubject = ValidationUtils._getQuerySubjectFromModuleByIdentifier(module, qsId);
				if (querySubject) {
					diagnostic = ValidationUtils._setDiagnoticProperties(querySubject, querySubject.getLabel(), message, 0);
					querySubject.addDiagnostic(diagnostic);
					var queryItem = ValidationUtils._getQueryItemFromQuerySubjectByIdentifier(querySubject, qiId);
					if (queryItem) {
						diagnostic = ValidationUtils._setDiagnoticProperties(queryItem, queryItem.getLabel(), message, 0);
						queryItem.addDiagnostic(diagnostic);
					}
				}
			}
		}
	}

	static _resolveAndTagRelationship(module, message) {
		var location = message.getLocation();
		if (location == null || location.indexOf("/relationship[") == -1) {
			return;
		}
		var locationList = ValidationUtils._convertLocationToList(location);
		var diagnostic;
		var obj = ValidationUtils._getObjectByLocationPath(module, locationList[0]);
		if (obj instanceof Relationship) {
			var relationship = obj;
			if (relationship.getLeft()) {
				var querySubject = ValidationUtils._getQuerySubjectFromModuleByIdentifier(module, relationship.getLeft().getRef());
				if (querySubject) {
					diagnostic = ValidationUtils._setDiagnoticProperties(relationship, relationship.getLabel(), message, querySubject.getFeatureID("relationship"));
					querySubject.addDiagnostic(diagnostic);
				}
			}
			if (relationship.getRight()) {
				var querySubject = ValidationUtils._getQuerySubjectFromModuleByIdentifier(module, relationship.getRight().getRef());
				if (querySubject) {
					diagnostic = ValidationUtils._setDiagnoticProperties(relationship, relationship.getLabel(), message, querySubject.getFeatureID("relationship"));
					querySubject.addDiagnostic(diagnostic);
				}
			}
		}
	}

	static _getQuerySubjectFromModuleByIdentifier(module, identifier) {
		if (!module) {
			return null;
		}
		var qsList = module.getQuerySubject();
		var querySubject;
		for (var __i_enFor0 = 0, __exp_enFor0 = qsList, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			if (qs.getIdentifier() == identifier) {
				querySubject = qs;
				break;
			}
		}
		return querySubject;
	}

	static _getQueryItemFromQuerySubjectByIdentifier(querySubject, identifier) {
		if (!querySubject) {
			return null;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getQueryItems(querySubject), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			if (qi.getIdentifier() == identifier) {
				return qi;
			}
		}
		return null;
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/ValidationUtils.js